import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { Board } from '../types';

interface CreateBoardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateBoard: (board: Omit<Board, 'id' | 'createdAt' | 'updatedAt'>) => void;
}

const CreateBoardModal: React.FC<CreateBoardModalProps> = ({
  isOpen,
  onClose,
  onCreateBoard,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [boardType, setBoardType] = useState<'product' | 'opportunity' | 'resource_manager'>('product');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) return;

    const boardData = {
      name: name.trim(),
      description: description.trim() || undefined,
      boardType,
      ownerId: '', // Will be set by the parent component
      members: [], // Will be set by the parent component
    };

    onCreateBoard(boardData);
    onClose();
    resetForm();
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setBoardType('product' as const);
  };

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-3xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Create New Board</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Board Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Board Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-field w-full"
                  placeholder="Enter board name"
                  required
                />
              </div>

              {/* Board Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Board Type *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setBoardType('product')}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                      boardType === 'product'
                        ? 'border-satsuma-500 bg-satsuma-50 text-satsuma-700'
                        : 'border-gray-200 bg-white/15 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-lg font-semibold mb-1">Product</div>
                      <div className="text-xs">Track product ideas and development</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBoardType('opportunity')}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                      boardType === 'opportunity'
                        ? 'border-satsuma-500 bg-satsuma-50 text-satsuma-700'
                        : 'border-gray-200 bg-white/15 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-lg font-semibold mb-1">Opportunity</div>
                      <div className="text-xs">Track RFPs and business opportunities</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBoardType('resource_manager')}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                      boardType === 'resource_manager'
                        ? 'border-satsuma-500 bg-satsuma-50 text-satsuma-700'
                        : 'border-gray-200 bg-white/15 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-lg font-semibold mb-1">Resource Manager</div>
                      <div className="text-xs">Projects, team allocation, and capacity</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input-field w-full resize-none"
                  rows={3}
                  placeholder="Describe what this board is for"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!name.trim()}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Board
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreateBoardModal;
